import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "cn"

const headingVariants = cva(
  "font-heading font-semibold tracking-tight text-balance",
  {
    variants: {
      type: {
        h1: "text-3xl/9",
        h2: "text-2xl/8",
        h3: "text-xl/7",
        h4: "text-lg/6",
        h5: "text-base/6",
        h6: "text-sm/5",
      },
    },
  }
)

type HeadingType = NonNullable<
  VariantProps<typeof headingVariants>["type"]
>

type HeadingTag = keyof React.JSX.IntrinsicElements

export type HeadingProps<TTag extends HeadingTag = "h2"> = Omit<
  React.ComponentPropsWithRef<TTag>,
  "type"
> & {
  /** Visual heading treatment, independent of the rendered HTML element. */
  type: HeadingType
  /** HTML element to render. Defaults to the element matching `type`. */
  tag?: TTag
}

/**
 * Polymorphic heading primitive with independently controlled visual and
 * semantic levels.
 *
 * When `tag` is omitted, the component renders the element named by `type`.
 * Set `tag` when document structure requires a different semantic element
 * without changing the visual hierarchy.
 *
 * @example
 * ```tsx
 * <Heading type="h1">Roster</Heading>
 * <Heading type="h1" tag="h2">Visually h1, semantically h2</Heading>
 * <Heading type="h3" tag="p">Paragraph rendered with h3 styling</Heading>
 * ```
 */
function Heading<TTag extends HeadingTag = "h2">({
  type,
  tag,
  className,
  ...props
}: HeadingProps<TTag>) {
  const Tag = (tag ?? type) as React.ElementType

  return (
    <Tag
      data-slot="heading"
      data-type={type}
      className={cn(headingVariants({ type }), className)}
      {...props}
    />
  )
}

export { Heading, headingVariants }
